/**
 * MarchingCubes - Extract mesh from voxel grid
 * 
 * Implements the marching cubes algorithm for isosurface extraction.
 */

import { BufferAttribute, BufferGeometry, Vector3 } from 'three';
import { edgeTable, triTable } from 'three/examples/jsm/objects/MarchingCubes.js';
import { VoxelGrid } from './VoxelGrid';

/**
 * Append-only float buffer backed by fixed-size Float32Array chunks. A plain
 * number[] costs ~8 bytes per element plus GC pressure — at fine cells a jaw
 * shell emits >20M coordinates, so the packed chunks cut hundreds of MB of
 * transient heap out of the extraction.
 */
class FloatChunks {
  private readonly chunkSize = 3 * (1 << 18); // divisible by 3 → chunks fill exactly
  private chunks: Float32Array[] = [];
  private used = 0;

  get length(): number {
    return (this.chunks.length === 0 ? 0 : (this.chunks.length - 1) * this.chunkSize) + this.used;
  }

  push3(a: number, b: number, c: number): void {
    let chunk = this.chunks[this.chunks.length - 1];
    if (!chunk || this.used + 3 > this.chunkSize) {
      chunk = new Float32Array(this.chunkSize);
      this.chunks.push(chunk);
      this.used = 0;
    }
    chunk[this.used++] = a;
    chunk[this.used++] = b;
    chunk[this.used++] = c;
  }

  concat(): Float32Array {
    const out = new Float32Array(this.length);
    let offset = 0;
    for (let i = 0; i < this.chunks.length; i++) {
      const filled = i === this.chunks.length - 1 ? this.used : this.chunkSize;
      out.set(this.chunks[i].subarray(0, filled), offset);
      offset += filled;
    }
    return out;
  }
}

// Corner offsets for a cube (standard marching cubes order)
const CORNER_OFFSETS: Array<[number, number, number]> = [
  [0, 0, 0], [1, 0, 0], [1, 1, 0], [0, 1, 0],
  [0, 0, 1], [1, 0, 1], [1, 1, 1], [0, 1, 1],
];

// Edge connections (which corners each edge connects)
const EDGE_CONNECTIONS: Array<[number, number]> = [
  [0, 1], [1, 2], [2, 3], [3, 0],
  [4, 5], [5, 6], [6, 7], [7, 4],
  [0, 4], [1, 5], [2, 6], [3, 7],
];

export interface MarchingCubesOptions {
  /** Iso-value for surface extraction (default: 0 for SDF) */
  isoValue?: number;
  /** Whether values < isoValue are inside (true for SDF, false for occupancy) */
  invertSign?: boolean;
  /** Compute vertex normals */
  computeNormals?: boolean;
}

/**
 * Extract mesh from voxel grid using marching cubes
 */
export function extractMesh(
  grid: VoxelGrid,
  options: MarchingCubesOptions = {},
): BufferGeometry {
  const {
    isoValue = 0,
    invertSign = true,  // Default for SDF (negative = inside)
    computeNormals = true,
  } = options;

  const { counts, origin, cellSize, strides } = grid.info;
  const positions = new FloatChunks();
  let vertexId = 0;

  const corners = CORNER_OFFSETS.map(() => new Vector3());
  const edgeVerts = EDGE_CONNECTIONS.map(() => new Vector3());

  // Flat-array offsets of the 8 cube corners (CORNER_OFFSETS order) so the hot
  // all-cells scan reads the field directly — no bounds-checked get() and no
  // per-cell allocation. Only surface-crossing cells reach the Vector3 work.
  const data = grid.data;
  const sx = strides.x;
  const sy = strides.y;
  const cornerFlat = CORNER_OFFSETS.map(([ox, oy, oz]) => ox * sx + oy * sy + oz);
  const cubeValues = new Array<number>(8);

  for (let ix = 0; ix < counts.x - 1; ix++) {
    for (let iy = 0; iy < counts.y - 1; iy++) {
      let base = ix * sx + iy * sy;
      for (let iz = 0; iz < counts.z - 1; iz++, base++) {
        let cubeIndex = 0;
        for (let i = 0; i < 8; i++) {
          const value = data[base + cornerFlat[i]];
          cubeValues[i] = value;
          const inside = invertSign ? value < isoValue : value > isoValue;
          if (inside) {
            cubeIndex |= 1 << i;
          }
        }

        // Skip empty/full cubes
        const edgeMask = Number((edgeTable as any)[cubeIndex] ?? 0);
        if (edgeMask === 0) continue;

        for (let i = 0; i < 8; i++) {
          const [ox, oy, oz] = CORNER_OFFSETS[i];
          corners[i].set(
            origin.x + (ix + ox) * cellSize,
            origin.y + (iy + oy) * cellSize,
            origin.z + (iz + oz) * cellSize,
          );
        }

        // Interpolate edge vertices
        for (let e = 0; e < 12; e++) {
          if (edgeMask & (1 << e)) {
            const [a, b] = EDGE_CONNECTIONS[e];
            const v1 = corners[a];
            const v2 = corners[b];
            const val1 = cubeValues[a];
            const val2 = cubeValues[b];
            const denom = val2 - val1;
            const t = Math.abs(denom) < 1e-6 ? 0.5 : (isoValue - val1) / denom;
            edgeVerts[e].copy(v1).lerp(v2, Math.max(0, Math.min(1, t)));
          }
        }

        // Generate triangles
        const triStart = cubeIndex * 16;
        for (let t = 0; t < 16; t += 3) {
          const a = (triTable as any)[triStart + t];
          if (a === -1 || a === undefined) break;
          const b = (triTable as any)[triStart + t + 1];
          const c = (triTable as any)[triStart + t + 2];

          const va = edgeVerts[a];
          const vb = edgeVerts[b];
          const vc = edgeVerts[c];

          positions.push3(va.x, va.y, va.z);
          positions.push3(vb.x, vb.y, vb.z);
          positions.push3(vc.x, vc.y, vc.z);
          vertexId += 3;
        }
      }
    }
  }

  if (positions.length === 0) {
    throw new Error('Marching cubes produced no geometry');
  }

  const geometry = new BufferGeometry();
  geometry.setAttribute('position', new BufferAttribute(positions.concat(), 3));
  // Unshared triangle-soup vertices → the index is the identity sequence.
  const index = new Uint32Array(vertexId);
  for (let i = 0; i < vertexId; i++) index[i] = i;
  geometry.setIndex(new BufferAttribute(index, 1));

  if (computeNormals) {
    geometry.computeVertexNormals();
  }

  return geometry;
}

/**
 * Extract shell between two iso-values (inner and outer surfaces)
 */
export function extractShell(
  grid: VoxelGrid,
  innerIso: number,
  outerIso: number,
  computeNormals = true,
): BufferGeometry {
  // Create a "shell" SDF where values between inner and outer are negative
  const shellGrid = grid.map((value) => {
    if (value >= innerIso && value <= outerIso) {
      // Inside the shell band
      const distToInner = value - innerIso;
      const distToOuter = outerIso - value;
      return -Math.min(distToInner, distToOuter);
    } else if (value < innerIso) {
      // Below inner surface
      return innerIso - value;
    } else {
      // Above outer surface
      return value - outerIso;
    }
  });

  return extractMesh(shellGrid, {
    isoValue: 0,
    invertSign: true,
    computeNormals,
  });
}

